import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import {
  createTaskInputObject,
  createTaskOutputSchema,
  taskTypeConfigShape,
} from "./create-task.step";

/**
 * Assignee and namespace belong to the task type. Create Typed Task must not
 * store or accept them: a value here would override the type's default.
 * `input` is the same kind of contradiction — the type's fields are
 * `taskInput.*` sockets, and a type with no schema rejects a free-form bag.
 */
export const TYPED_TASK_TYPE_OWNED_OVERRIDE_KEYS = [
  "assigneeType",
  "assigneeUserId",
  "assigneeUserGroupIds",
  "assigneeContactIds",
  "assigneeAgentDefinitionId",
  "taskNamespaceKey",
  "taskNamespaceId",
  "input",
] as const;

/** Drop type-owned overrides. Returns the same object when none are present. */
export function withoutTypedTaskTypeOwnedOverrides<T extends object>(
  value: T,
): T {
  const record = value as Record<string, unknown>;
  if (
    !TYPED_TASK_TYPE_OWNED_OVERRIDE_KEYS.some((key) =>
      Object.prototype.hasOwnProperty.call(record, key),
    )
  ) {
    return value;
  }
  const next = { ...record };
  for (const key of TYPED_TASK_TYPE_OWNED_OVERRIDE_KEYS) {
    delete next[key];
  }
  return next as T;
}

/**
 * Create Typed Task
 *
 * Creates a task of one task type chosen on the node. Unlike Create Task, the
 * type is not an input, so it cannot be overridden at run time. The selected
 * type's anchor entity and input-schema fields therefore become sockets, and
 * required schema fields must be wired before the workflow can run.
 * Assignee and namespace stay on the task type: this node shows them and
 * does not accept overrides.
 *
 * `reuseExistingAnchoredTask` returns the open task for this type and anchor
 * when one exists. A new task is still created (and the input schema still
 * enforced) when the slot is empty.
 *
 * `reopenPreviouslyClosedTask` only applies with that lookup. When no open
 * task holds the slot, the most recently closed task for the same type and
 * anchor is reopened instead of creating another.
 *
 * `leaveClosedAnchoredTask` is the other closed-task choice, and it cannot
 * be combined with reopen. When no open task holds the slot, that same
 * closed task is returned unchanged: it stays closed, and no new task is
 * created. A new task is still created when none exists at all.
 */
export const createTypedTaskStep = defineStepConfig({
  type: "create_typed_task",
  name: "Create Typed Task",
  description:
    "Create a task of a selected task type. That type's input fields become sockets, and required fields must be wired. Assignee and namespace come from the task type. Can return the task that already exists for this type and anchor, reopen one that was previously closed, or leave a closed task alone.",
  category: "Tasks",

  inputSchema: createTaskInputObject
    .omit({
      taskTypeKey: true,
      assigneeType: true,
      assigneeUserId: true,
      assigneeAgentDefinitionId: true,
      taskNamespaceId: true,
      // The type either requires its anchor through sourceEntity, or it is
      // not anchored. A separate anchor socket cannot add a type the
      // definition does not declare.
      anchorEntity: true,
      input: true,
    })
    .catchall(z.unknown()),

  outputSchema: createTaskOutputSchema,

  // The key is required here. The type's input fields are not: they live on
  // the task type row, and the save and activation paths resolve that row
  // before generating sockets. This schema cannot see the database.
  // Assignee and namespace are absent on purpose: a plain object strips them,
  // so a saved override cannot win over the task type.
  configSchema: z
    .object({
      ...taskTypeConfigShape,
      reuseExistingAnchoredTask: z
        .boolean()
        .optional()
        .describe(
          "When true, return the existing open task for this task type and anchor entity instead of creating another. When none exists, create one and still enforce the type's input schema, unless a previously closed task is reopened or left as-is. Has no effect for a task type that does not declare an anchor.",
        ),
      reopenPreviouslyClosedTask: z
        .boolean()
        .optional()
        .describe(
          "When finding a previously created task and no open task holds this type and anchor, reopen the most recently closed one instead of creating another. Requires the lookup option. Cannot be combined with leaving a closed task alone. Has no effect for a task type that does not declare an anchor.",
        ),
      leaveClosedAnchoredTask: z
        .boolean()
        .optional()
        .describe(
          "When finding a previously created task and no open task holds this type and anchor, return the most recently closed one unchanged instead of creating or reopening another. Requires the lookup option. Cannot be combined with reopening. Has no effect for a task type that does not declare an anchor.",
        ),
    })
    .superRefine((value, ctx) => {
      if (
        typeof value.taskTypeKey !== "string" ||
        value.taskTypeKey.trim() === ""
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["taskTypeKey"],
          message:
            "A task type is required. This node creates tasks of one type, and that type's input fields are required sockets.",
        });
      }
      if (
        value.reopenPreviouslyClosedTask === true &&
        value.reuseExistingAnchoredTask !== true
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["reopenPreviouslyClosedTask"],
          message:
            "Reopening a previously closed task requires finding the task for this type and anchor.",
        });
      }
      if (
        value.leaveClosedAnchoredTask === true &&
        value.reuseExistingAnchoredTask !== true
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["leaveClosedAnchoredTask"],
          message:
            "Leaving a closed task alone requires finding the task for this type and anchor.",
        });
      }
      if (
        value.leaveClosedAnchoredTask === true &&
        value.reopenPreviouslyClosedTask === true
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["leaveClosedAnchoredTask"],
          message: "A closed task can be reopened or left alone, not both.",
        });
      }
    }),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  emits: [SystemEvent.TASK_CREATED, SystemEvent.TASK_STATUS_CHANGED] as const,
});
